import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { APP_CONFIG, SKIP_401_RETRY } from 'libs/shared/app-config/src/lib/gb-app-config'
import { EMPTY, Observable, throwError } from 'rxjs'
import { catchError, filter, switchMap, take } from 'rxjs/operators'
import { GbBaseAuthService } from 'libs/shared/utils/services/src/lib/gb-base-auth-service/gb-base-auth.service'
import { Action, ActionCreator, Store } from '@ngrx/store'

/**
 * Base interceptor which handles attaching an auth header to HTTP requests
 * for authenticated users, and refreshing the token as necessary.
 */
@Injectable()
export abstract class BaseAuthInterceptor<T> implements HttpInterceptor {
  isRefreshing = false // flag to prevent multiple refresh token requests

  constructor(
    protected store: Store,
    protected gbBaseAuthService: GbBaseAuthService,
    private refreshTokenAction: ActionCreator<string, () => Action>,
    @Inject(APP_CONFIG) protected appConfig: any,
  ) {}

  abstract intercept(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>>

  protected handleRequest(modifiedReq: HttpRequest<T>, next: HttpHandler, token?: string): Observable<HttpEvent<T>> {
    const req = token ? modifiedReq.clone({ setHeaders: { Authorization: `Token ${token}` } }) : modifiedReq

    return next.handle(req).pipe(catchError((error) => this.handleError(error, modifiedReq, next, token)))
  }

  private handleError(error: HttpErrorResponse, modifiedReq: HttpRequest<T>, next: HttpHandler, token?: string) {
    if (error.status === 401) {
      if (this.isRefreshUrl(modifiedReq.url)) {
        this.isRefreshing = false
        return throwError(() => error) // logout handled by refreshTokenFailure effect
      }

      const prevToken = token || this.gbBaseAuthService.accessTokenSubjectValue // fallback to subject if cookie removed
      const skipRetry = modifiedReq.context.get(SKIP_401_RETRY)

      if (!prevToken && !skipRetry) {
        return throwError(() => error)
      }

      if (!this.isRefreshing) {
        this.isRefreshing = true
        this.store.dispatch(this.refreshTokenAction())
      }

      return this.gbBaseAuthService.accessToken$.pipe(
        filter((newToken) => !!newToken && newToken !== prevToken),
        take(1),
        switchMap((newToken) => {
          this.isRefreshing = false

          // skip handling request after token refreshed as refresh handles setting the authentication
          if (skipRetry) return EMPTY

          return this.handleRequest(modifiedReq, next, newToken)
        }),
      )
    }

    return throwError(() => error)
  }

  private isRefreshUrl(url: string): boolean {
    return url.includes(this.appConfig.refreshTokenUrl)
  }
}
